/**
 * KNK-1.54 / FR-46 — deactivating a rep: the rules and copy behind
 * Settings → Knock Mode → Field profiles → Deactivate.
 *
 * ⚠️ NOTHING HERE IS THE GATE. `deactivate_field_rep`, `reactivate_field_rep` and
 * `resolve_field_deactivation_quarantine` (migrations 20261010a1, c3, d3) refuse everything this
 * screen hides. The gate is `lead.manage` AT EVERYONE SCOPE, which this file cannot see — the
 * screen therefore OFFERS the action to everyone who can open Field settings and lets the server
 * answer, rather than guessing at a permission it does not hold. A refusal arrives as the
 * server's own sentence, which is written for the person reading it.
 *
 * ⚠️ Two states that look alike and are not:
 *   `active: false` with no deactivation  — a seasonal rep whose active-until has passed. Nothing
 *                                           moved; nothing to settle.
 *   a deactivation                        — their open Revisits and Claims went to another rep,
 *                                           and Knocks their phone had queued are held for 72 h.
 * `fieldStateLabel` is the one place that distinction is turned into words, and the test asserts
 * on those words, because this screen is where a manager finds out which one they are looking at.
 */

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const int = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0);

// -----------------------------------------------------------------------------
// The open deactivation carried on a field profile
// -----------------------------------------------------------------------------

export interface FieldDeactivation {
  deactivationId: string;
  deactivatedAt: string;
  quarantineUntil: string;
  /** The server's own reading of the 72 hours, not a clock comparison done here. */
  windowOpen: boolean;
  movedToRepId: string | null;
  /** Knocks still waiting on a verdict. */
  held: number;
}

export function parseDeactivation(raw: unknown): FieldDeactivation | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const deactivationId = str(r.deactivation_id);
  const deactivatedAt = str(r.deactivated_at);
  const quarantineUntil = str(r.quarantine_until);
  if (!deactivationId || !deactivatedAt || !quarantineUntil) return null;
  return {
    deactivationId,
    deactivatedAt,
    quarantineUntil,
    windowOpen: r.window_open === true,
    movedToRepId: str(r.moved_to_rep_id),
    held: int(r.held),
  };
}

// -----------------------------------------------------------------------------
// The preview
// -----------------------------------------------------------------------------

export interface DeactivationMoves {
  openRevisits: number;
  openTimedRevisits: number;
  standingClaims: number;
  openSessions: number;
  /** Reported so the preview can say what does NOT move. */
  knocks: number;
}

export interface DeactivationCandidate {
  userId: string;
  name: string;
}

export interface DeactivationPreview {
  userId: string;
  alreadyDeactivated: boolean;
  moves: DeactivationMoves;
  candidates: DeactivationCandidate[];
}

export function parsePreview(raw: unknown): DeactivationPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const userId = str(r.user_id);
  if (!userId) return null;
  const m = (r.moves && typeof r.moves === 'object' ? r.moves : {}) as Record<string, unknown>;
  return {
    userId,
    alreadyDeactivated: r.already_deactivated === true,
    moves: {
      openRevisits: int(m.open_revisits),
      openTimedRevisits: int(m.open_timed_revisits),
      standingClaims: int(m.standing_claims),
      openSessions: int(m.open_sessions),
      knocks: int(m.knocks),
    },
    candidates: (Array.isArray(r.candidates) ? r.candidates : []).flatMap((c) => {
      const x = c as Record<string, unknown>;
      const id = str(x?.user_id);
      return id ? [{ userId: id, name: str(x.name) ?? 'Unnamed' }] : [];
    }),
  };
}

/** Is a target rep required? The server refuses without one when anything would move. */
export function targetRequired(moves: DeactivationMoves): boolean {
  return moves.openRevisits > 0 || moves.standingClaims > 0;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
/** ⚠️ The VERB has to agree too. "1 open Revisit move" shipped for about a minute. */
const verb = (n: number, singular: string, plural_: string) => (n === 1 ? singular : plural_);

/**
 * What the confirm step says will move. One line per kind, only the kinds that have anything,
 * and always the line about what stays — a manager's first question about deactivating a rep is
 * whether the Doors go with them, and the answer is no.
 */
export function movesLines(moves: DeactivationMoves): string[] {
  const lines: string[] = [];
  if (moves.openRevisits > 0) {
    const timed = moves.openTimedRevisits;
    const detail = timed > 0 && timed < moves.openRevisits ? ` (${timed} with a time set)` : '';
    lines.push(`${plural(moves.openRevisits, 'open Revisit')}${detail} ${verb(moves.openRevisits, 'moves', 'move')} to the rep you choose.`);
  }
  if (moves.standingClaims > 0) {
    lines.push(`${plural(moves.standingClaims, 'standing Door Claim')} ${verb(moves.standingClaims, 'moves', 'move')} to the rep you choose.`);
  }
  if (moves.openSessions > 0) {
    lines.push(`${plural(moves.openSessions, 'open knocking session')} ${verb(moves.openSessions, 'ends', 'end')} now.`);
  }
  lines.push(
    moves.knocks > 0
      ? `${plural(moves.knocks, 'Knock')} and every Door stay in this account under their name. Nothing is reassigned or deleted.`
      : 'Every Door stays in this account. Nothing is reassigned or deleted.',
  );
  return lines;
}

/** Nothing to move: the confirm step still has to say what happens, which is the phone half. */
export function nothingToMove(moves: DeactivationMoves): boolean {
  return moves.openRevisits === 0 && moves.standingClaims === 0;
}

// -----------------------------------------------------------------------------
// The quarantine
// -----------------------------------------------------------------------------

export type QuarantineState = 'held' | 'accepted' | 'discarded';

export interface QuarantinedKnock {
  knockId: string;
  pinId: string;
  address: string | null;
  disposition: string;
  category: string | null;
  deviceAt: string;
  receivedAt: string | null;
  localDay: string | null;
  late: boolean;
  state: QuarantineState;
}

export interface Quarantine {
  deactivationId: string;
  userId: string;
  deactivatedAt: string;
  quarantineUntil: string;
  windowOpen: boolean;
  knocks: QuarantinedKnock[];
}

const STATES: readonly QuarantineState[] = ['held', 'accepted', 'discarded'];

export function parseQuarantine(raw: unknown): Quarantine | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const deactivationId = str(r.deactivation_id);
  const userId = str(r.user_id);
  const deactivatedAt = str(r.deactivated_at);
  const quarantineUntil = str(r.quarantine_until);
  if (!deactivationId || !userId || !deactivatedAt || !quarantineUntil) return null;
  return {
    deactivationId,
    userId,
    deactivatedAt,
    quarantineUntil,
    windowOpen: r.window_open === true,
    knocks: (Array.isArray(r.knocks) ? r.knocks : []).flatMap((k) => {
      const x = k as Record<string, unknown>;
      const knockId = str(x?.knock_id);
      const pinId = str(x?.pin_id);
      const deviceAt = str(x?.device_at);
      if (!knockId || !pinId || !deviceAt) return [];
      return [{
        knockId,
        pinId,
        address: str(x.address),
        disposition: str(x.disposition) ?? str(x.status_code) ?? 'Unknown',
        category: str(x.category),
        deviceAt,
        receivedAt: str(x.received_at),
        localDay: str(x.local_day),
        late: x.late === true,
        state: STATES.includes(x.state as QuarantineState) ? (x.state as QuarantineState) : 'held',
      }];
    }),
  };
}

export const heldKnocks = (q: Quarantine): QuarantinedKnock[] => q.knocks.filter((k) => k.state === 'held');

/**
 * The quarantine's own sentence. ⚠️ A closed window does NOT mean the held Knocks are gone: they
 * are still held and still need a verdict. What closes is the rep's ability to send more. Saying
 * "expired" here would read as "already dealt with", which is the opposite of what is true.
 */
export function quarantineLine(q: Quarantine): string {
  const held = heldKnocks(q).length;
  const nothing = held === 0;
  if (q.windowOpen) {
    return nothing
      ? 'Their phone can still send Knocks it logged before they were deactivated. Nothing is waiting on you yet.'
      : `${plural(held, 'Knock')} logged before they were deactivated ${held === 1 ? 'is' : 'are'} waiting for you to accept or discard. Their phone can still send more.`;
  }
  return nothing
    ? 'Their phone can no longer send Knocks, and nothing was left waiting.'
    : `Their phone can no longer send Knocks. ${plural(held, 'Knock')} ${held === 1 ? 'is' : 'are'} still waiting for you to accept or discard.`;
}

// -----------------------------------------------------------------------------
// The list of deactivations
// -----------------------------------------------------------------------------

export interface DeactivationRow {
  deactivationId: string;
  userId: string;
  name: string;
  deactivatedAt: string;
  deactivatedByName: string | null;
  quarantineUntil: string;
  windowOpen: boolean;
  movedToRepId: string | null;
  movedToName: string | null;
  revisitsMoved: number;
  claimsMoved: number;
  sessionsEnded: number;
  reactivatedAt: string | null;
  held: number;
  quarantined: number;
}

export function parseDeactivationRows(raw: unknown): DeactivationRow[] {
  return (Array.isArray(raw) ? raw : []).flatMap((row) => {
    const r = row as Record<string, unknown>;
    const deactivationId = str(r?.deactivation_id);
    const userId = str(r?.user_id);
    const deactivatedAt = str(r?.deactivated_at);
    const quarantineUntil = str(r?.quarantine_until);
    if (!deactivationId || !userId || !deactivatedAt || !quarantineUntil) return [];
    return [{
      deactivationId,
      userId,
      name: str(r.name) ?? 'Unnamed',
      deactivatedAt,
      deactivatedByName: str(r.deactivated_by_name),
      quarantineUntil,
      windowOpen: r.window_open === true,
      movedToRepId: str(r.moved_to_rep_id),
      movedToName: str(r.moved_to_name),
      revisitsMoved: int(r.revisits_moved),
      claimsMoved: int(r.claims_moved),
      sessionsEnded: int(r.sessions_ended),
      reactivatedAt: str(r.reactivated_at),
      held: int(r.held),
      quarantined: int(r.quarantined),
    }];
  });
}

// -----------------------------------------------------------------------------
// The one place the two kinds of "inactive" are turned into words
// -----------------------------------------------------------------------------

export type FieldState = 'active' | 'inactive' | 'deactivated';

export function fieldState(p: { active: boolean; deactivation: FieldDeactivation | null }): FieldState {
  if (p.deactivation) return 'deactivated';
  return p.active ? 'active' : 'inactive';
}

export function fieldStateLabel(p: { active: boolean; deactivation: FieldDeactivation | null }): string {
  switch (fieldState(p)) {
    case 'deactivated':
      return 'Deactivated';
    // ⚠️ Not "Deactivated". Nothing moved and nothing is held; it is a date on their profile.
    case 'inactive':
      return 'Not active in the field';
    default:
      return 'Active';
  }
}

export const FIELD_DEACTIVATION_COPY = {
  deactivate: 'Deactivate',
  reactivate: 'Reactivate',
  deactivateTitle: 'Deactivate in the field',
  deactivateDescription:
    'They stop being able to log Knocks in this account, and their phone signs out and clears this account\'s data the next time it reaches the server.',
  chooseTarget: 'Who takes their open Revisits and Door Claims?',
  chooseTargetHint: 'An active rep in this account who can log Knocks.',
  noCandidates:
    'There is no other active rep in this account to take the work. Add one, or reactivate someone, before deactivating this rep.',
  nothingToMove: 'They have no open Revisits and no standing Door Claims, so there is nothing to hand over.',
  confirm: 'Deactivate',
  cancel: 'Cancel',
  reactivateTitle: 'Reactivate in the field',
  reactivateDescription:
    'They can log Knocks again. What was handed over stays where it is — move anything back by hand.',
  reactivateBlocked:
    'Accept or discard their held Knocks first. Reactivating now would leave Knocks held against a deactivation that no longer exists.',
  quarantineTitle: 'Knocks waiting for a verdict',
  accept: 'Accept',
  discard: 'Discard',
  acceptAll: 'Accept all',
  discardAll: 'Discard all',
  acceptHint: 'An accepted Knock counts and shows on the Door, under their name.',
  discardHint: 'A discarded Knock stays in the history as undone and counts for nothing.',
  noHeld: 'Nothing is waiting.',
  // ⚠️ Load-bearing: this is the only place the product says Knocks are not reassigned, which is
  // FR-46's first consequence and the question a manager asks first.
  knocksStay: 'Doors and Knocks stay in this account under the rep\'s name.',
} as const;
